import type { AxiosResponse } from 'axios';
import type { DeepReadonly, ShallowRef } from 'vue';

import { defineStore } from 'pinia';
// @ts-nocheck
import Vue from 'vue';

import type { SignalRMethodName } from '@/constants/signalr-methods';

import { signalrMethods } from '@/constants/signalr-methods';
import { defaultBroadcastServiceSender } from '@/utils/broadcast-service';
import { formatPhone } from '@/utils/helpers';

import { companyBilling, companyInvestmentStats, profileGet } from '@/api/endpoints/user-company';

type Nullable<T> = T | null;
export type RoleSide = 'investor' | 'borrower';
interface SwitchCompanyPayload { role?: Nullable<RoleSide>, companyId?: Nullable<string> }

const billingAccountTypeBySide = {
  investor: 'INV',
  borrower: 'DBT',
} as const;
type BillingAccountType = typeof billingAccountTypeBySide[keyof typeof billingAccountTypeBySide] | 'AUTO';

interface BillingWalletAccount extends Record<string, any> {
  tp: BillingAccountType
}

// FIXME: в константы
const investorRegistrationTasksTypes = [
  'investor_reg_pp',
  'investor_reg_le',
  // legacy below
  'investor_reg_physic',
  'investor_reg_entity',
];
// FIXME: в константы
const investorProfileRegistrationTasksTypes = ['invest_profile_config_le', 'invest_profile_config_pp'];

interface State {
  companyTypesByRoles: DeepReadonly<Record<RoleSide, string[]>>

  serverData?: ShallowRef<Record<string, any> | null | undefined>
  userid?: Nullable<string>
  sessionExpired: boolean
  phone?: Nullable<string>
  permissionsAccess?: Nullable<Array<{ name: string }>>
  companiesList: Array<Record<string, any>> // FIXME: переделать в объект Record<RoleSide, UserCompany[]>
  investorAppTask?: Nullable<Record<string, any>>
  investorProfileAppTask?: Nullable<Array<{ name: string }>>
  borrowerProfileAppTask?: Nullable<Array<{ name: string }>>
  borrowerAppTask?: Nullable<Record<string, any>>
  avatarLink?: Nullable<string>
  roleSide?: Nullable<RoleSide>
  companyId?: Nullable<string>
  billingData?: Nullable<Record<string, any>>
  isGuestMode: boolean
  totalUnreadTickets: number
  deviceIdSupported: boolean
  isAuthTokenValid: boolean

  userDismissQualifiedInvestorCard: boolean
}

// FIXME: переделать - нужно частичное обновление каких-то полей
function updateBillingData(this: ReturnType<typeof useProfileStore>, data: AxiosResponse['data']) {
  const fired_utc_at = new Date(data.fired_utc_at || Date.now());
  if (this.billingData?.fired_utc_at && this.billingData.fired_utc_at > fired_utc_at) {
    return;
  }

  const tp = this.roleBasedAccType;
  if (!tp) {
    this.billingData = null;
    return;
  }

  const incomingBillingData = data.response;

  if (incomingBillingData?.acc?.findIndex((el: BillingWalletAccount) => el.tp === tp) !== -1) {
    this.billingData = {
      ...incomingBillingData,
      fired_utc_at,
    };
  }
  else {
    this.billingData = null;
  }
}

const lsDismissQualifiedInvestorCardKey = 'qualified-investor-card:dismiss';

export const useProfileStore = defineStore('profile', {
  state: (): State => ({
    serverData: shallowRef(null),

    userid: null,
    sessionExpired: false,
    phone: null,

    permissionsAccess: null,

    companiesList: [],

    investorAppTask: null,
    investorProfileAppTask: null,
    borrowerAppTask: null,
    borrowerProfileAppTask: null,

    roleSide: null,
    companyId: null,
    avatarLink: null,

    billingData: null,

    companyTypesByRoles: readonly({
      borrower: ['borr.UL', 'borr.IP', 'borr'], // "borr" = как заглушка при реге, placeholder для метки будущей роли
      investor: ['invest.FL', 'invest.UL', 'invest.IP', 'invest'], // "invest" = ... так же метка для будущей роли
    }),

    isGuestMode: false,
    totalUnreadTickets: 0,

    userDismissQualifiedInvestorCard: window?.localStorage?.getItem(lsDismissQualifiedInvestorCardKey)?.toLowerCase() === 'true',

    deviceIdSupported: true,
    /*
    теперь tnx может быть живым с точки зрения куки, но инвалидирован на сервере --
    требуется более тонкий контроль (раньше истекшая сессия не позволяла получить данные по profile/get,
    теперь это возможно, и мы потеряли возможность по ответу понимать, что сессия просрочена, поэтому будет новый флаг на сервере)*/
    isAuthTokenValid: false,
  }),

  getters: {
    selectedCompanyData(state): Record<string, any> | null | undefined {
      const roleSide = state.roleSide;
      const companyId = state.companyId;
      if (!roleSide || !companyId)
        return null;

      return state.companiesList.find((company) => {
        return company.id === companyId && state.companyTypesByRoles[roleSide].includes(company.type);
      });
    },

    roleBasedAccType(state): BillingAccountType | null {
      const side = state.roleSide;
      if (!side)
        return null;

      return billingAccountTypeBySide[side];
    },

    billingWalletAccountsByTypes(state): Record<BillingAccountType, BillingWalletAccount> | null {
      const billingDataAcc = state.billingData?.acc as BillingWalletAccount[] | undefined;
      if (!billingDataAcc)
        return null;

      return Object.fromEntries(billingDataAcc.map((acc) => [acc.tp, acc])) as Record<BillingAccountType, BillingWalletAccount>;
    },

    investorAppRegState(state) {
      const regState = state.investorAppTask?.state;
      if (!regState || regState === 'done') {
        return regState;
      }

      const lastTaskState = state.investorAppTask?.last_task_state;
      if (
        ['irf_checked', 'irf_considering', 'irf_autocheck_failed', 'irf_autocheck_success', 'iru_checked', 'iru_considering', 'iru_autocheck_failed', 'iru_autocheck_success', 'iru_fin_pass_err', 'irf_fin_pass_err'].includes(lastTaskState)
      ) {
        return 'pending';
      }

      if (['irf_waitcoin', 'iru_waitcoin'].includes(lastTaskState)) {
        return 'bank_confirm';
      }

      return regState;
    },

    borrowerAppRegState(state): string | undefined {
      return state.borrowerAppTask?.state;
    },

    borrowerReturnToFill(): boolean {
      return this.borrowerAppRegState !== 'done';
    },

    investorReturnToFill(): boolean {
      return this.investorAppRegState == null;
    },

    companyRegStateCompleted(state): boolean | null {
      // TODO: сделать util для определения статусов регистрации роли

      const roleSide = state.roleSide;
      if (!roleSide)
        return null;

      if (roleSide === 'borrower') {
        return this.borrowerAppRegState === 'done';
      }
      else if (roleSide === 'investor') {
        return ['done', 'bank_confirm'].includes(this.investorAppRegState);
      }

      return false;
    },

    isCurrentQualifiedInvestor(state): boolean | null {
      if (state.roleSide !== 'investor')
        return null;

      if (!this.selectedCompanyData)
        return null;

      if (this.selectedCompanyData.is_qualified_investor === true) {
        return true;
      }

      return false;
    },

    canBeQualifiedInvestor(): boolean | null {
      if (this.investorAppRegState !== 'done')
        return false;

      if (!this.selectedCompanyData)
        return null;

      if (this.selectedCompanyData.type !== 'invest.FL')
        return false;

      return this.isCurrentQualifiedInvestor === false;
    },

    isAuthenticated(state): boolean {
      return Boolean(state.userid && state.isAuthTokenValid);
    },

    avatarServerFile(state) {
      if (!state.selectedCompanyData)
        return;

      return state.selectedCompanyData?.docs.find((doc: { type: string }) => doc.type === 'user-avatar');
    },

    investors(state): typeof state.companiesList {
      return state.companiesList?.filter((company) =>
        this.companyTypesByRoles.investor.includes(company.type)
        // костыль от ситуаций, когда на бэке недореганная компания светится и список фактически непустой, но задачи на таске нет
        && company.taskmanager_task_id !== undefined
      ) ?? [];
    },

    borrowers(state): typeof state.companiesList {
      return state.companiesList?.filter((company) =>
        this.companyTypesByRoles.borrower.includes(company.type)
        && company.taskmanager_task_id !== undefined
      ) ?? [];
    },

    dismissQualifiedInvestorCard(): boolean {
      return !this.canBeQualifiedInvestor || this.userDismissQualifiedInvestorCard;
    },

    visibleName(state): string | null {
      if (!state.serverData)
        return null;

      const output = this.companyRegStateCompleted === true && this.selectedCompanyData?.name;
      return output || formatPhone(state.phone);
    },

    availableReferralPage(): boolean {
      if (!this.companyRegStateCompleted)
        return false;

      return this.selectedCompanyData?.type === 'invest.FL';
    },

    hasQuickAccessSettings(): boolean {
      return this.serverData?.pin_settings !== undefined;
    },

    isQuickAccessOn(): boolean {
      return this.serverData?.pin_settings?.isQuickAccessOn;
    },

    hasPinSet(): boolean {
      return this.serverData?.pin_settings?.hasPinSet;
    },

    activeSessionsCount(): number {
      return this.serverData?.active_sessions;
    }
  },

  actions: {
    onSignalrEvent({ event, data }: { event: SignalRMethodName, data?: any }) {
      if (event === signalrMethods.ticketNewMessage) {
        this.totalUnreadTickets = data.total_unread;
      }
      else if (event === signalrMethods.balanceChange) {
        if (data.companyid === this.companyId && this.billingData && data.balance != null) {
          const billingDataAccItemById = this.billingData.acc.find((el: BillingWalletAccount) => el.id === data.id);
          if (billingDataAccItemById) {
            billingDataAccItemById.balance = data.balance;
          }
        }
      }
    },

    convertCompanyTypeToRoleSide(companyType: string): RoleSide | null {
      if (!companyType)
        return null;

      if (this.companyTypesByRoles.investor.includes(companyType)) {
        return 'investor';
      }

      if (this.companyTypesByRoles.borrower.includes(companyType)) {
        return 'borrower';
      }

      return null;
    },

    findAnyCompanyByRole(role: RoleSide) {
      return this.companiesList.find((company) => this.companyTypesByRoles[role].includes(company.type));
    },

    /**
     * Переключение между компаниями (инвестор/заемщик) пользователя.
     * Если payload не задан, то берёт первую из списка
     */
    // FIXME: получать быстро список всех актуальных компаний перед сменой
    async switchToCompany(payload?: Nullable<SwitchCompanyPayload>) {
      let role: SwitchCompanyPayload['role'], companyId: SwitchCompanyPayload['companyId'];

      payload && ({ role, companyId } = payload);

      if (role && !companyId) {
        // ищем первую попавшуюся компанию по роли
        companyId = this.findAnyCompanyByRole(role)?.id;
      }
      else if (companyId) {
        // ищем роль по ид компании
        role = this.convertCompanyTypeToRoleSide(this.companiesList.find((company) => company.id === companyId)?.type);
      }

      this.roleSide = role;
      this.companyId = role ? companyId : null;

      // TODO: проверять что companyid/role изменились и только тогда вызывать fetch?
      if (!this.userid) {
        await this.fetchServerData();
      }

      this.fetchBillingAccount();
      this.cacheAvatarImage();
    },

    async fetchServerData() {
      // FIXME: посмотреть что именно делает, и упразднить по возможности - слишком перегруженный

      this.billingData = null;
      return Vue.http.get(profileGet).then(async (wrapper) => {
        this.sessionExpired = false;

        const apiResponse = wrapper.data.response;
        this.serverData = apiResponse;

        const newUserid = apiResponse?.userid;

        this.userid = newUserid;
        this.phone = apiResponse?.phone;

        this.isAuthTokenValid = apiResponse?.isAuthenticated;

        this.totalUnreadTickets = apiResponse?.total_unread_tickets;

        this.companiesList = apiResponse?.companys || [];

        if (apiResponse?.tasks) {
          let borrowerTask = null;
          let investorTask = null;
          const investorProfileTask = [];
          const borrowerProfileTask = [];
          for (const task of apiResponse.tasks) {
            if (!borrowerTask && task.type === 'borrower_reg') {
              borrowerTask = task;
            }
            else if (!investorTask && investorRegistrationTasksTypes.includes(task.type)) {
              investorTask = task;
            }
            else if (investorProfileRegistrationTasksTypes.includes(task.type)) {
              investorProfileTask.push(task);
            }
            else if ('borrower_profile_config'.includes(task.type)) {
              borrowerProfileTask.push(task);
            }
          }

          this.investorAppTask = investorTask;
          this.borrowerAppTask = borrowerTask;
          if (investorProfileTask.length === 1) {
            this.investorProfileAppTask = investorProfileTask[0];
          }
          else {
            const filtered = investorProfileTask.sort((b, a) => new Date(a.dadd).getTime() - new Date(b.dadd).getTime());
            this.investorProfileAppTask = filtered[0];
          }
          if (borrowerProfileTask.length === 1) {
            this.borrowerProfileAppTask = borrowerProfileTask[0];
          }
          else {
            const filtered = borrowerProfileTask.sort((b, a) => new Date(a.dadd).getTime() - new Date(b.dadd).getTime());
            this.borrowerProfileAppTask = filtered[0];
          }
        }
        else {
          this.borrowerAppTask = this.investorAppTask = null;
        }

        if (!this.companyId || this.companiesList.findIndex((company) => company.id === this.companyId) === -1) {
          this.companyId = null;
        }

        if (this.companyId) {
          this.fetchBillingAccount();
        }

        this.cacheAvatarImage();

        return wrapper;
      });
    },

    async cacheAvatarImage() {
      if (this.avatarLink)
        URL.revokeObjectURL(this.avatarLink);

      if (!this.avatarServerFile?.link) {
        this.avatarLink = null;
        return;
      }
      return fetch(this.avatarServerFile.link)
        .then((r) => r.blob())
        .then((blob) => {
          this.avatarLink = URL.createObjectURL(blob);
        });
    },

    async fetchBillingAccount() {
      return Vue.http
        .get(companyBilling, {
          globalLoader: false,
          shouldRetry: (response) => {
            if (!this.companyId || !this.companyRegStateCompleted || response?.data.status === 'err') {
              return false;
            }

            return !response?.data?.response?.acc?.length;
          },
        })
        .then((response) => {
          if (!response.data) {
            return Promise.reject(response);
          }

          return updateBillingData.call(this, response.data);
        })
        .catch((error) => {
          this.billingData = null;
          return Promise.reject(error);
        });
    },

    deactivateSession() {
      this.sessionExpired = true;
    },

    setGuestModeAvailable(value: boolean) {
      this.isGuestMode = !this.isAuthenticated && value;
    },

    hideDismissQualifiedInvestorCard() {
      this.userDismissQualifiedInvestorCard = true;
      window?.localStorage?.setItem(lsDismissQualifiedInvestorCardKey, 'true');
    },

    fetchInvestmentStats(tabInfo?: string) {
      return Vue.http.post(companyInvestmentStats, { period: tabInfo || 'all' }, { globalLoader: false }).then(({ data }) => data.response);
    },

    setDeviceIdSupported(value: boolean) {
      this.deviceIdSupported = value;
    },

    onLogout() {
      defaultBroadcastServiceSender?.postMessage({ type: 'logout' });
      this.$reset();
    },
  },
});
